import { GripVertical, MoreHorizontal } from "lucide-react";
import { useEffect, useLayoutEffect, useRef } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { centsEur } from "@/lib/documents";
import { parseNumber } from "@/lib/format";
import type { DocumentInput, Position } from "@/lib/types";
import { cn } from "@/lib/utils";
import { SourceChip } from "./SourceChip";
import { useAutosave } from "./useDocument";

/** Borderless until hovered or focused: the position reads like the document, and every text is editable in place. */
export const GHOST =
  "block w-[calc(100%+0.5rem)] -ml-2 rounded-md border border-transparent bg-transparent px-2 py-[3px] text-inherit outline-none transition-[border-color,background-color,box-shadow] duration-150 hover:border-border hover:bg-card focus-visible:border-ring focus-visible:bg-card focus-visible:ring-[3px] focus-visible:ring-ring/25 read-only:hover:border-transparent read-only:hover:bg-transparent aria-invalid:border-destructive motion-reduce:transition-none pointer-coarse:min-h-11";

const qty = (n: number) => new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(n);
const price = (cents: number) => new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);

function numberSave(save: (n: number) => Promise<unknown>) {
  return (text: string) => {
    const n = parseNumber(text);
    return n === null ? Promise.reject(new Error("Bitte eine Zahl eingeben")) : save(n);
  };
}

/** A textarea that grows with its text. */
function GrowingTextarea(props: React.ComponentProps<"textarea">) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [props.value]);
  return <textarea ref={ref} rows={1} {...props} className={cn("resize-none overflow-hidden", props.className)} />;
}

export interface PositionRowProps {
  position: Position;
  n: number;
  offerNumber: string | null;
  highlighted: boolean;
  autoFocus: boolean;
  /** Where a dragged position would land. */
  drop: "before" | "after" | null;
  save: (input: DocumentInput) => Promise<unknown>;
  onHover: (id: number | null) => void;
  onFocusChange: (id: number | null) => void;
  canMove: { up: boolean; down: boolean };
  onMove: (delta: -1 | 1) => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  onDragEnd: () => void;
}

export function PositionRow(p: PositionRowProps) {
  const { position: pos, n } = p;
  const row = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const one = (fields: Omit<NonNullable<DocumentInput["positions"]>[number], "id">) => p.save({ positions: [{ id: pos.id, ...fields }] });
  const manual = pos.source === null;
  const deduction = pos.source?.kind === "deduction";

  const title = useAutosave(pos.title, (title) => one({ title }));
  const description = useAutosave(pos.description, (description) => one({ description }));
  const quantity = useAutosave(qty(pos.quantity), numberSave((quantity) => one({ quantity })));
  const unit = useAutosave(pos.unit, (unit) => one({ unit }));
  const unitPrice = useAutosave(price(pos.unitPriceCents), numberSave((unitPrice) => one({ unitPrice })));
  const errors = [title, description, quantity, unit, unitPrice].map((f) => f.error).filter((e): e is string => e !== null);

  useEffect(() => {
    if (p.autoFocus) titleRef.current?.focus();
  }, [p.autoFocus]);

  return (
    <div
      ref={row}
      role="listitem"
      onMouseEnter={() => p.onHover(pos.id)}
      onMouseLeave={() => p.onHover(null)}
      onFocus={() => p.onFocusChange(pos.id)}
      onBlur={(e) => {
        if (!row.current?.contains(e.relatedTarget as Node | null)) p.onFocusChange(null);
      }}
      onDragOver={p.onDragOver}
      onDrop={(e) => {
        e.preventDefault();
        p.onDrop();
      }}
      className={cn(
        "group/pos relative flex flex-wrap items-start gap-x-2.5 gap-y-1.5 border-t py-2.5 pr-3 pl-2 transition-colors duration-150 first:border-t-0 motion-reduce:transition-none",
        p.highlighted && "bg-hl/45",
        p.drop === "before" && "before:absolute before:inset-x-0 before:-top-px before:h-0.5 before:bg-ring",
        p.drop === "after" && "after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-ring",
      )}
    >
      <button
        ref={handleRef}
        type="button"
        aria-label="Position verschieben"
        aria-keyshortcuts="ArrowUp ArrowDown"
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", String(pos.id));
          if (row.current) e.dataTransfer.setDragImage(row.current, 16, 16);
          p.onDragStart();
        }}
        onDragEnd={p.onDragEnd}
        onKeyDown={(e) => {
          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            e.preventDefault();
            p.onMove(e.key === "ArrowUp" ? -1 : 1);
            // Moving the row in the DOM can drop focus; keep it on the handle for the next arrow press.
            requestAnimationFrame(() => handleRef.current?.focus());
          }
        }}
        className="mt-0.5 flex h-7 w-6 shrink-0 cursor-grab items-center justify-center rounded-md text-muted-foreground opacity-0 outline-none transition-opacity duration-150 group-hover/pos:opacity-100 group-focus-within/pos:opacity-100 focus-visible:ring-[3px] focus-visible:ring-ring/50 active:cursor-grabbing motion-reduce:transition-none pointer-coarse:mt-0 pointer-coarse:size-11 pointer-coarse:opacity-100"
      >
        <GripVertical aria-hidden className="size-3.5" />
      </button>
      <span className="w-5 shrink-0 pt-[5px] text-[0.8125rem] text-muted-foreground tabular-nums">{n}.</span>
      <div className="flex min-w-0 flex-[1_1_12rem] flex-col gap-0.5">
        <input
          ref={titleRef}
          aria-label={`Titel Position ${n}`}
          value={title.value}
          readOnly={deduction}
          aria-invalid={title.error !== null || undefined}
          onChange={(e) => title.change(e.target.value)}
          onFocus={title.focus}
          onBlur={title.blur}
          className={cn(GHOST, "text-sm font-semibold")}
        />
        <GrowingTextarea
          aria-label={`Beschreibung Position ${n}`}
          value={description.value}
          readOnly={deduction}
          aria-invalid={description.error !== null || undefined}
          onChange={(e) => description.change(e.target.value)}
          onFocus={description.focus}
          onBlur={description.blur}
          className={cn(GHOST, "text-[0.8125rem] leading-[1.45] text-muted-foreground focus-visible:text-foreground")}
        />
      </div>
      <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-x-2.5 gap-y-1.5 pt-0.5">
        <SourceChip source={pos.source} offerNumber={p.offerNumber} />
        {manual && (
          <span className="flex items-center gap-1 text-[0.8125rem] text-muted-foreground">
            <input
              aria-label={`Menge Position ${n}`}
              inputMode="decimal"
              value={quantity.value}
              aria-invalid={quantity.error !== null || undefined}
              onChange={(e) => quantity.change(e.target.value)}
              onFocus={quantity.focus}
              onBlur={quantity.blur}
              className={cn(GHOST, "ml-0 w-14 text-right tabular-nums")}
            />
            <input
              aria-label={`Einheit Position ${n}`}
              value={unit.value}
              aria-invalid={unit.error !== null || undefined}
              onChange={(e) => unit.change(e.target.value)}
              onFocus={unit.focus}
              onBlur={unit.blur}
              className={cn(GHOST, "ml-0 w-12")}
            />
            <span aria-hidden>×</span>
            <input
              aria-label={`Preis Position ${n}`}
              inputMode="decimal"
              value={unitPrice.value}
              aria-invalid={unitPrice.error !== null || undefined}
              onChange={(e) => unitPrice.change(e.target.value)}
              onFocus={unitPrice.focus}
              onBlur={unitPrice.blur}
              className={cn(GHOST, "ml-0 w-20 text-right tabular-nums")}
            />
            <span aria-hidden>€</span>
          </span>
        )}
        <span className="w-21 text-right text-sm font-medium tabular-nums">{centsEur(pos.totalCents)}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={`Weitere Aktionen für Position ${n}`} className="text-muted-foreground pointer-coarse:size-11">
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled={!p.canMove.up} onSelect={() => p.onMove(-1)}>Nach oben</DropdownMenuItem>
            <DropdownMenuItem disabled={!p.canMove.down} onSelect={() => p.onMove(1)}>Nach unten</DropdownMenuItem>
            <DropdownMenuItem variant="destructive" onSelect={() => void one({ delete: true }).catch((e: Error) => toast.error(e.message))}>Entfernen</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {errors.length > 0 && (
        <p role="alert" className="basis-full pl-[3.25rem] text-xs text-destructive">{errors[0]}</p>
      )}
    </div>
  );
}
